import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { PassCard, BadgePill, Drawer } from '@/components/campus';
import {
  Ticket,
  Calendar,
  Clock,
  MapPin,
  Loader2,
  PartyPopper,
  QrCode,
  ChevronRight,
  Download,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format helpers
const formatEventDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  if (date.toDateString() === today.toDateString()) {
    return 'Tonight';
  } else if (date.toDateString() === tomorrow.toDateString()) {
    return 'Tomorrow';
  }
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const formatEventTime = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// Status badge mapping
const statusConfig = {
  valid: { label: 'VALID', variant: 'primary', color: 'text-electric-ice-blue' },
  used: { label: 'CHECKED IN', variant: 'success', color: 'text-cool-mint' },
  revoked: { label: 'REVOKED', variant: 'error', color: 'text-signal-red' },
  pending: { label: 'PENDING', variant: 'warning', color: 'text-soft-amber' },
};

// ==================== PASS CARD PREVIEW ====================
const PassPreviewCard = ({ pass, onClick }) => {
  const config = statusConfig[pass.status] || statusConfig.valid;
  const isUpcoming = new Date(pass.event_date) > new Date();
  
  return (
    <div
      onClick={onClick}
      className="group cursor-pointer rounded-2xl border border-white/[0.08] bg-gradient-to-b from-graphite to-obsidian overflow-hidden transition-all duration-300 hover:border-electric-ice-blue/30 hover:shadow-lg hover:shadow-electric-ice-blue/5"
      data-testid={`pass-card-${pass.id}`}
    >
      <div className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-electric-ice-blue/10 flex items-center justify-center">
              <QrCode className="w-6 h-6 text-electric-ice-blue" />
            </div>
            <div>
              <h3 className="font-semibold text-cloud-white group-hover:text-electric-ice-blue transition-colors line-clamp-1">
                {pass.event_name}
              </h3>
              <p className="text-sm text-muted-text">{pass.guest_name}</p>
            </div>
          </div>
          <BadgePill variant={config.variant} size="sm">
            {config.label}
          </BadgePill>
        </div>
        
        {/* Event Info */}
        <div className="flex flex-wrap items-center gap-3 text-soft-silver text-sm">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-4 h-4 text-electric-ice-blue" />
            <span>{formatEventDate(pass.event_date)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-cool-mint" />
            <span className="font-mono">{formatEventTime(pass.event_date)}</span>
          </div>
        </div>
        
        {pass.event_location && (
          <div className="flex items-center gap-1.5 text-sm text-soft-silver">
            <MapPin className="w-4 h-4 text-soft-amber" />
            <span className="truncate">{pass.event_location}</span>
          </div>
        )}
        
        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-white/[0.06]">
          <span className={cn("text-xs font-mono", config.color)}>
            {pass.status === 'used' 
              ? `Checked in ${formatDateTime(pass.checked_in_at)}`
              : isUpcoming 
                ? 'Tap to view QR code'
                : 'Event passed'
            }
          </span>
          <ChevronRight className="w-5 h-5 text-muted-text group-hover:text-electric-ice-blue group-hover:translate-x-1 transition-all" />
        </div>
      </div>
    </div>
  );
};

// ==================== PASS DETAIL DRAWER ====================
const PassDetailDrawer = ({ pass, isOpen, onClose }) => {
  if (!pass) return null;
  
  const config = statusConfig[pass.status] || statusConfig.valid;
  
  const handleDownload = () => {
    const qrContainer = document.querySelector('[data-testid="pass-card"] .bg-white');
    if (!qrContainer) return;
    
    const svg = qrContainer.querySelector('svg');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      const padding = 40;
      canvas.width = 300 + padding * 2;
      canvas.height = 400 + padding * 2;
      
      ctx.fillStyle = '#0A0A0B';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.fillStyle = 'white';
      ctx.fillRect(padding, padding, 300, 300);
      
      ctx.drawImage(img, padding + 50, padding + 50, 200, 200);
      
      ctx.fillStyle = 'white';
      ctx.font = 'bold 18px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(pass.guest_name, canvas.width / 2, 380);
      
      ctx.font = '14px Inter, sans-serif';
      ctx.fillStyle = '#8B8B8B';
      ctx.fillText(pass.event_name || 'Event', canvas.width / 2, 405);
      
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `pass-${pass.event_name?.replace(/\s+/g, '-') || 'event'}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      toast.success('Pass downloaded');
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };
  
  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Your Pass" width="lg">
      <div className="space-y-6">
        {/* Pass Card with QR */}
        <PassCard
          guestName={pass.guest_name}
          eventName={pass.event_name}
          eventDate={formatDateTime(pass.event_date)}
          eventLocation={pass.event_location}
          qrCodeData={pass.qr_code_data}
          status={pass.status}
          checkedInAt={pass.checked_in_at ? formatDateTime(pass.checked_in_at) : undefined}
          createdAt={formatDateTime(pass.created_at)}
          size="lg"
        />
        
        {/* Actions */}
        {pass.status === 'valid' && (
          <Button
            onClick={handleDownload}
            className="w-full bg-electric-ice-blue/10 text-electric-ice-blue border border-electric-ice-blue/20 hover:bg-electric-ice-blue/20"
          >
            <Download className="w-4 h-4 mr-2" />
            Download Pass
          </Button>
        )}
        
        {/* Status Messages */}
        {pass.status === 'used' && (
          <div className="p-4 bg-cool-mint/10 rounded-lg border border-cool-mint/20 text-center">
            <p className="text-cool-mint text-sm">
              You checked in at {formatDateTime(pass.checked_in_at)}
            </p>
          </div>
        )}
        
        {pass.status === 'revoked' && (
          <div className="p-4 bg-signal-red/10 rounded-lg border border-signal-red/20 text-center">
            <p className="text-signal-red text-sm">
              This pass has been revoked
            </p>
          </div>
        )}
        
        {/* Instructions */}
        {pass.status === 'valid' && (
          <div className="p-4 bg-obsidian/50 rounded-lg border border-white/[0.06] text-center">
            <p className="text-soft-silver text-sm">
              Show this QR code at the door for check-in
            </p>
          </div>
        )}
      </div>
    </Drawer>
  );
};

// ==================== PENDING RSVPS SECTION ====================
const PendingRsvps = ({ rsvps }) => {
  if (rsvps.length === 0) return null;
  
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-cloud-white">Pending Requests</h2>
      <div className="space-y-3">
        {rsvps.map(rsvp => (
          <div 
            key={rsvp.id}
            className="p-4 rounded-xl border border-soft-amber/20 bg-soft-amber/5"
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-medium text-cloud-white">{rsvp.event_name}</h3>
                <p className="text-sm text-soft-silver mt-1">
                  {formatEventDate(rsvp.event_date)} at {formatEventTime(rsvp.event_date)}
                </p>
              </div>
              <BadgePill variant="warning" size="sm">PENDING</BadgePill>
            </div>
            <p className="text-xs text-muted-text mt-2">
              Waiting for approval from the host
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== MAIN MY PASS PAGE ====================
export const MyPass = () => {
  const { session, user } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [passes, setPasses] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPass, setSelectedPass] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  
  // Fetch passes
  const fetchPasses = useCallback(async () => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }
    
    try {
      const [passesRes, rsvpsRes] = await Promise.all([
        fetch(`${API_URL}/api/discover/my-passes`, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        }),
        fetch(`${API_URL}/api/discover/my-rsvps`, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        })
      ]);
      
      if (passesRes.ok) {
        const data = await passesRes.json();
        setPasses(data.passes || []);
      }
      
      if (rsvpsRes.ok) {
        const data = await rsvpsRes.json();
        // Filter to only pending RSVPs without passes
        const pending = (data.rsvps || []).filter(
          r => r.approval_status === 'pending' && !r.has_pass
        );
        setRsvps(pending);
      }
    } catch (error) {
      console.error('Failed to fetch passes:', error);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);
  
  useEffect(() => {
    fetchPasses();
  }, [fetchPasses]);
  
  // Redirect if not logged in
  if (!session && !loading) {
    return (
      <div className="text-center py-16">
        <Ticket className="w-16 h-16 text-muted-text mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-cloud-white mb-2">Sign in to view your passes</h2>
        <p className="text-soft-silver mb-6">
          Access your event passes and check-in history
        </p>
        <Button
          onClick={() => navigate('/login')}
          className="bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
        >
          Sign In
        </Button>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
          <p className="text-soft-silver text-sm">Loading your passes...</p>
        </div>
      </div>
    );
  }
  
  // Separate active and past passes
  const now = new Date();
  const activePasses = passes.filter(p => p.status === 'valid' && new Date(p.event_date) >= now);
  const usedPasses = passes.filter(p => p.status === 'used');
  const pastPasses = passes.filter(p => p.status === 'valid' && new Date(p.event_date) < now);
  
  const hasAnyPasses = passes.length > 0 || rsvps.length > 0;
  
  return (
    <div className="space-y-8 pb-12" data-testid="my-pass-page">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-cool-mint/10 rounded-full border border-cool-mint/20">
          <Ticket className="w-4 h-4 text-cool-mint" />
          <span className="text-sm text-cool-mint font-medium">My Passes</span>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-cloud-white">
          Your Event Passes
        </h1>
        <p className="text-soft-silver max-w-md mx-auto">
          View your QR passes and check-in history
        </p>
      </div>
      
      {!hasAnyPasses ? (
        /* Empty State */
        <div className="text-center py-16">
          <div className="w-20 h-20 rounded-2xl bg-electric-ice-blue/10 flex items-center justify-center mx-auto mb-4">
            <Ticket className="w-10 h-10 text-electric-ice-blue/50" />
          </div>
          <h2 className="text-xl font-semibold text-cloud-white mb-2">No passes yet</h2>
          <p className="text-soft-silver mb-6 max-w-sm mx-auto">
            Discover events and request passes to see them here
          </p>
          <Button
            onClick={() => navigate('/discover')}
            className="bg-cool-mint text-obsidian hover:bg-cool-mint/90"
          >
            <PartyPopper className="w-4 h-4 mr-2" />
            Discover Events
          </Button>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Pending RSVPs */}
          <PendingRsvps rsvps={rsvps} />
          
          {/* Active Passes */}
          {activePasses.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-cloud-white flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-cool-mint animate-pulse" />
                Active Passes
              </h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {activePasses.map(pass => (
                  <PassPreviewCard
                    key={pass.id}
                    pass={pass}
                    onClick={() => {
                      setSelectedPass(pass);
                      setIsDetailOpen(true);
                    }}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Used Passes (Check-in History) */}
          {usedPasses.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-cloud-white">Check-in History</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {usedPasses.map(pass => (
                  <PassPreviewCard
                    key={pass.id}
                    pass={pass}
                    onClick={() => {
                      setSelectedPass(pass);
                      setIsDetailOpen(true);
                    }}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Past/Expired Passes */}
          {pastPasses.length > 0 && (
            <div className="space-y-4 opacity-60">
              <h2 className="text-lg font-semibold text-cloud-white">Past Events</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {pastPasses.map(pass => (
                  <PassPreviewCard
                    key={pass.id}
                    pass={pass}
                    onClick={() => {
                      setSelectedPass(pass);
                      setIsDetailOpen(true);
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Pass Detail Drawer */}
      <PassDetailDrawer
        pass={selectedPass}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
      />
    </div>
  );
};

export default MyPass;
